import React, { useState, useEffect, useMemo } from 'react';
import {
  Package, Search, X, CheckCircle, Calendar, User,
  Building2, Eye, ChevronLeft, ChevronRight, Filter,
  ArrowUpDown, FileText, Loader2, AlertCircle, RotateCcw
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import ACCOUNTING_API_BASE from '../AccountingAPI';

/* ──────────────────────────────────────────────
   CONSTANTS
   ────────────────────────────────────────────── */
const PAGE_SIZES = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

/* ──────────────────────────────────────────────
   HELPERS
   ────────────────────────────────────────────── */
const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateLong = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/* ──────────────────────────────────────────────
   BADGE
   ────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    1: { label: 'Active', bg: 'bg-emerald-100', text: 'text-emerald-700' },
    2: { label: 'Approved', bg: 'bg-blue-100', text: 'text-blue-700' },
    3: { label: 'Closed', bg: 'bg-gray-100', text: 'text-gray-600' },
    '-2': { label: 'Reverted', bg: 'bg-orange-100', text: 'text-orange-700' },
  };
  const s = map[status] || map[1];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <CheckCircle className="w-3.5 h-3.5" />
      {s.label}
    </span>
  );
};

/* ──────────────────────────────────────────────
   MAIN COMPONENT
   ────────────────────────────────────────────── */
export default function GRViewList() {
  /* ── data ── */
  const [grList, setGrList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ── filters ── */
  const [searchText, setSearchText] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);


  const [postedGRNumbers, setPostedGRNumbers] = useState(new Set());
  const [postingGRId, setPostingGRId] = useState(null);
  const [postedPONumbers, setPostedPONumbers] = useState(new Set());

  const fetchPostedGRNumbers = async () => {
    try {
      const res = await fetch(`${ACCOUNTING_API_BASE}/GoodReceipt`);
      if (!res.ok) return;
      const data = await res.json();

      // ✅ Kolektahin lahat ng purchaseOrderNumber na may GR na sa accounting
      // Para malaman kung yung PO ng isang GR ay naka-post na
      const postedPONumbers = new Set(
        (Array.isArray(data) ? data : [])
          .map(g => g.purchaseOrderNumber)
          .filter(Boolean)
      );

      // ✅ I-check din yung grNumber para sa direct GR match
      const postedGRNums = new Set(
        (Array.isArray(data) ? data : [])
          .map(g => g.grNumber)
          .filter(Boolean)
      );

      setPostedGRNumbers(postedGRNums);
      setPostedPONumbers(postedPONumbers); // ← bagong state
    } catch (error) {
      console.error('Error fetching posted GR numbers:', error);
    }
  };

  useEffect(() => {
    fetchGRList();
    fetchPostedGRNumbers(); // ✅ dagdag
  }, []);

  const handlePostGRToAccounting = async (gr) => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: "Post GR to Accounting",
      html: `Post <strong>${gr.grNo}</strong> to the accounting system?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Post Now",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#059669",
    });
    if (!isConfirmed) return;

    setPostingGRId(gr.id);
    try {
      // ✅ STEP 1: Kunin muna lahat ng PO sa accounting API
      let accountingPurchaseOrderId = 0;

      if (gr.poNumber) {
        const poRes = await fetch(`${ACCOUNTING_API_BASE}/PurchaseOrder`);
        if (!poRes.ok) throw new Error(`Failed to fetch POs from accounting: ${poRes.status}`);
        const poList = await poRes.json();

        // ✅ Try exact match muna
        let matchedPO = (Array.isArray(poList) ? poList : [])
          .find(p => (p.poNumber || '').trim() === (gr.poNumber || '').trim());

        // ✅ Kung walang exact match, try yung reference field
        if (!matchedPO) {
          matchedPO = (Array.isArray(poList) ? poList : [])
            .find(p => (p.reference || '').trim() === (gr.poNumber || '').trim());
        }

        if (!matchedPO) {
          await Swal.fire({
            icon: 'warning',
            title: 'PO Not Found in Accounting',
            html: `PO <strong>${gr.poNumber}</strong> does not exist in the accounting system yet.<br/><br/>
             <small class="text-gray-500">Please post the Purchase Order to accounting first before posting this GR.</small>`,
            confirmButtonColor: '#d97706',
          });
          return;
        }

        accountingPurchaseOrderId = matchedPO.id;
      }

      // ✅ STEP 3: I-build ang GR payload gamit yung totoong purchaseOrderId mula sa accounting
      const goodReceiptDetails = (gr.grDetails || []).map(item => {
        const subTotal = (item.receivedQty || 0) * (item.unitPrice || 0);
        const taxAmount = subTotal * 0.12;
        const totalAmount = subTotal - (item.totalDiscount || 0) + taxAmount;

        return {
          id: 0,
          goodsReceiptHeaderId: 0,
          itemCode: item.itemCode || "",
          orderedQty: item.orderedQty || 0,
          receivedQty: item.receivedQty || 0,
          remainingQty: (item.orderedQty || 0) - (item.receivedQty || 0),
          unitPrice: item.unitPrice || 0,
          subTotal: subTotal,
          taxationCodeId: 0,
          totalDiscount: item.totalDiscount || 0,
          taxAmount: taxAmount,
          totalAmount: totalAmount,
        };
      });

      const totalAmount = goodReceiptDetails.reduce((sum, d) => sum + d.totalAmount, 0);

      const payload = {
        goodReceiptDetails,
        grNumber: gr.grNo,
        purchaseOrderId: accountingPurchaseOrderId, // ← yung ID mula sa accounting API
        amount: totalAmount,
        grDate: gr.grDate || new Date().toISOString(),
        uploadedFile: "",
        notes: gr.remarks || "",
        status: "Received",
        createdDate: gr.grDate || new Date().toISOString(),
        createdBy: 0,
      };

      // ✅ STEP 4: I-POST ang GR sa accounting
      const res = await fetch(`${ACCOUNTING_API_BASE}/GoodReceipt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Accounting API error ${res.status}: ${errText}`);
      }

      // ✅ Mark as posted locally
      setPostedGRNumbers(prev => new Set([...prev, gr.grNo]));

      // Log
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "POST-ACCOUNTING",
          module: "Goods Receipt",
          description: `Posted GR ${gr.grNo} to accounting. PO: ${gr.poNumber || 'Manual'} (Accounting PO ID: ${accountingPurchaseOrderId}), Items: ${goodReceiptDetails.length}`,
          userId: user?.id || user?.userId,
          username: user?.username,
          fullName: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || user?.username,
        }),
      }).catch(err => console.error("Log failed:", err));

      await Swal.fire({
        icon: "success",
        title: "Posted Successfully!",
        html: `<strong>${gr.grNo}</strong> has been posted to the accounting system.`,
        confirmButtonColor: "#059669",
        timer: 2500,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error("Error posting GR to accounting:", error);
      await Swal.fire({
        icon: "error",
        title: "Post Failed",
        html: `<strong>${gr.grNo}</strong> failed to post.<br/><small>${error.message}</small>`,
      });
    } finally {
      setPostingGRId(null);
    }
  };
  /* ── sort ── */
  const [sortKey, setSortKey] = useState('grDate');
  const [sortDir, setSortDir] = useState('desc');

  /* ── pagination ── */
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  /* ── detail modal ── */
  const [selectedGR, setSelectedGR] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /* ── delete/revert loading per row ── */
  const [deletingId, setDeletingId] = useState(null);
  const [revertingId, setRevertingId] = useState(null);




  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportVendor, setExportVendor] = useState('');
  // ── add this state at the top with the others ──
  const [vendorMap, setVendorMap] = useState({});
  const [poMap, setPoMap] = useState({});

  // ── replace the convertVendorNames useEffect with this ──
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [poRes, vendorRes] = await Promise.all([
          fetch(`${API_BASE_URL}api/T_PO_Header`),
          fetch(`${API_BASE_URL}api/Vendor`),
        ]);
        const poListData = await poRes.json();
        const vendorListData = await vendorRes.json();

        const newPoMap = {};
        (Array.isArray(poListData) ? poListData : []).forEach(po => {
          const key = (po.poNumber || '').trim();
          if (key) newPoMap[key] = po;
        });

        const newVendorMap = {};
        (Array.isArray(vendorListData) ? vendorListData : []).forEach(v => {
          newVendorMap[v.id] = v.VendorName || v.vendorName || '';
          newVendorMap[String(v.id)] = v.VendorName || v.vendorName || '';
        });

        setPoMap(newPoMap);
        setVendorMap(newVendorMap);
      } catch (err) {
        console.error('Failed to load vendor/PO lookups:', err);
      }
    };

    loadLookups();
  }, []); // ✅ runs ONCE on mount only
  const resolveVendorName = (gr) => {
    const po = poMap[(gr.poNumber || '').trim()] || {};
    const vendorId = po.vendorId || gr.vendorId || 0;
    return vendorMap[vendorId] || vendorMap[String(vendorId)] || gr.vendorName || '';
  };
  const fetchExportData = async () => {
    setExportLoading(true);
    try {
      const rows = [];
      for (const gr of grList) {
        const supplierName = resolveVendorName(gr);
        const po = poMap[(gr.poNumber || '').trim()] || {};
        const requestDate = po.poCreatedDate || '';
        const requiredDeliveryDate = po.deliveryDate || '';
        const details = gr.grDetails || [];

        if (details.length > 0) {
          details.forEach(detail => {
            rows.push({
              grNo: gr.grNo || '',
              grDate: gr.grDate || '',
              vendorName: supplierName,
              poNumber: gr.poNumber || '',
              requestDate,
              requiredDeliveryDate,
              actualDeliveryDate: gr.grDate || '',
              supplierName,
              itemCode: detail.itemCode || '',
              description: detail.itemDescription || '',
              poQty: detail.orderedQty || 0,
              actualDelivered: detail.receivedQty || 0,
              balanceToDeliver: (detail.orderedQty || 0) - (detail.receivedQty || 0),
              place: '',
              remarks: gr.remarks || '',
              status: gr.status,
            });
          });
        } else {
          rows.push({
            grNo: gr.grNo || '',
            grDate: gr.grDate || '',
            vendorName: supplierName,
            poNumber: gr.poNumber || '',
            requestDate,
            requiredDeliveryDate,
            actualDeliveryDate: gr.grDate || '',
            supplierName,
            itemCode: '', description: '', poQty: 0,
            actualDelivered: 0, balanceToDeliver: 0,
            place: '', remarks: gr.remarks || '',
            status: gr.status,
          });
        }
      }
      setExportData(rows);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed to load data', text: err.message });
    } finally {
      setExportLoading(false);
    }
  };
  const applyPeriodFilter = (rows) => {
    let filtered = [...rows];
    if (exportVendor) filtered = filtered.filter(r => r.vendorName === exportVendor);

    if (exportPeriodType === 'week' && exportWeek) {
      const [yr, wk] = exportWeek.split('-W').map(Number);
      filtered = filtered.filter(r => {
        const d = new Date(r.grDate);
        const jan1 = new Date(d.getFullYear(), 0, 1);
        const weekNo = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
        return d.getFullYear() === yr && weekNo === wk;
      });
    } else if (exportPeriodType === 'month' && exportMonth) {
      const [yr, mo] = exportMonth.split('-').map(Number);
      filtered = filtered.filter(r => {
        const d = new Date(r.grDate);
        return d.getFullYear() === yr && d.getMonth() + 1 === mo;
      });
    } else if (exportPeriodType === 'year' && exportYear) {
      filtered = filtered.filter(r => new Date(r.grDate).getFullYear() === Number(exportYear));
    }

    return filtered;
  };

  const [exportPeriodType, setExportPeriodType] = useState('month');
  const [exportWeek, setExportWeek] = useState('');
  const [exportMonth, setExportMonth] = useState('');
  const [exportYear, setExportYear] = useState(new Date().getFullYear().toString());
  const handleExportExcel = () => {
    let rows = [...exportData];

    // Apply filters
    rows = applyPeriodFilter(rows);
    if (rows.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No Data', text: 'Walang data na ma-e-export sa filters na pinili.' });
      return;
    }

    const wb = XLSX.utils.book_new();

    // ── Header rows
    const wsData = [
      ['GR / PURCHASE ORDER MONITORING REPORT'],
      [`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`],
      [], // blank row
      [
        'REQUEST DATE', 'REQUIRED DELIVERY DATE', 'ACTUAL DELIVERY DATE',
        'SUPPLIER NAME', 'PO NUMBER', 'ITEM CODE', 'DESCRIPTION',
        'PO QUANTITY', 'ACTUAL DELIVERED', 'BALANCE TO DELIVER',
        'PLACE', 'REMARKS'
      ],
      ...rows.map(r => [
        r.requestDate ? new Date(r.requestDate).toLocaleDateString('en-US') : '',
        r.requiredDeliveryDate ? new Date(r.requiredDeliveryDate).toLocaleDateString('en-US') : '',
        r.actualDeliveryDate ? new Date(r.actualDeliveryDate).toLocaleDateString('en-US') : '',
        r.supplierName,
        r.poNumber,
        r.itemCode,
        r.description,
        r.poQty,
        r.actualDelivered,
        r.balanceToDeliver,
        r.place,
        r.remarks,
      ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column widths
    ws['!cols'] = [
      { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 16 },
      { wch: 14 }, { wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 22 }
    ];

    // ── Merge title row A1:L1
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 11 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 11 } },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'GR-PO Report');
    XLSX.writeFile(wb, `GRPO_Report_${new Date().toISOString().split('T')[0]}.xlsx`);

    Swal.fire({
      icon: 'success', title: 'Exported!',
      text: `${rows.length} row(s) na-export successfully.`,
      timer: 2000, showConfirmButton: false
    });
  };
  /* ────────────── fetch list ────────────── */
  useEffect(() => {
    fetchGRList();
  }, []);

  const fetchGRList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/GR_Header`);
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      setGrList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load Goods Receipts');
    } finally {
      setLoading(false);
    }
  };

  /* ────────────── unique vendor names for filter dropdown ────────────── */
  const vendorOptions = useMemo(() => {
    const set = new Set(grList.map((g) => g.vendorName).filter(Boolean));
    return [...set].sort();
  }, [grList]);

  /* ────────────── filtered + sorted data ────────────── */
  const filtered = useMemo(() => {
    let data = [...grList];

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter(
        (g) =>
          g.grNo?.toLowerCase().includes(q) ||
          g.vendorName?.toLowerCase().includes(q) ||
          g.poNumber?.toLowerCase().includes(q) ||
          g.receivedBy?.toLowerCase().includes(q)
      );
    }

    if (filterVendor) {
      data = data.filter((g) => g.vendorName === filterVendor);
    }

    if (filterStatus !== '') {
      data = data.filter((g) => g.status === Number(filterStatus));
    }

    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      data = data.filter((g) => new Date(g.grDate) >= from);
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59);
      data = data.filter((g) => new Date(g.grDate) <= to);
    }

    data.sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];
      if (sortKey === 'grDate') {
        valA = new Date(valA);
        valB = new Date(valB);
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [grList, searchText, filterVendor, filterStatus, filterDateFrom, filterDateTo, sortKey, sortDir]);

  /* ────────────── paginated slice ────────────── */
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice(
    (safeCurrentPage - 1) * pageSize,
    safeCurrentPage * pageSize
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, filterVendor, filterStatus, filterDateFrom, filterDateTo, pageSize]);

  /* ────────────── sort toggle ────────────── */
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />;
    return (
      <span className="inline-flex">
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  /* ────────────── open detail modal ────────────── */
  const openDetail = async (grId) => {
    setDetailLoading(true);
    setSelectedGR(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/GR_Header/${grId}`);
      if (!res.ok) throw new Error('Failed to load GR details');
      const data = await res.json();
      setSelectedGR(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  /* ────────────── REVERT ────────────── */
  /* ────────────── REVERT ────────────── */
  /* ────────────── REVERT ────────────── */
  const handleRevert = async (gr, closeModalAfter = false) => {
    // Fetch latest PO details using poId (the actual integer FK)
    let poDetails = [];
    if (gr.poId) {
      try {
        const poRes = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${gr.poId}`);
        if (poRes.ok) {
          const raw = await poRes.json();
          poDetails = Array.isArray(raw) ? raw : [raw];
        }
      } catch (err) {
        console.error('Failed to fetch PO details:', err);
      }
    }

    // Build map: itemCode -> poDetail (latest from DB)
    const poDetailMap = {};
    poDetails.forEach(pd => {
      poDetailMap[pd.itemCode] = pd;
    });

    const inputFieldsHTML = gr.grDetails && gr.grDetails.length > 0 ? `
  <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 max-h-96 overflow-y-auto">
    <p class="font-bold text-blue-800 mb-3 flex items-center gap-2">
      <span>📋</span> Enter quantities to revert:
    </p>
    <table class="w-full text-xs">
      <thead class="bg-blue-100 sticky top-0">
        <tr>
          <th class="px-2 py-1 text-left font-semibold text-blue-900">Item Code</th>
          <th class="px-2 py-1 text-right font-semibold text-amber-700">Ordered Qty</th>
          <th class="px-2 py-1 text-right font-semibold text-blue-900">GR Received</th>
          <th class="px-2 py-1 text-right font-semibold text-indigo-700">PO Remaining (Latest)</th>
          <th class="px-2 py-1 text-center font-semibold text-blue-900">Qty to Revert</th>
          <th class="px-2 py-1 text-right font-semibold text-green-700">Remaining After Revert</th>
          <th class="px-2 py-1 text-left font-semibold text-blue-900">Warehouse</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-blue-100">
        ${gr.grDetails.map((item, index) => {
      const grReceivedQty = item.receivedQty ?? 0;
      const pd = poDetailMap[item.itemCode];

      // ✅ CORRECT: use pd.qty and pd.totalReceivedQty (latest from DB)
      const orderedQty = pd ? (pd.qty ?? 0) : (item.orderedQty ?? 0);
      const totalReceived = pd ? (pd.totalReceivedQty ?? 0) : 0;
      const poRemainingQty = pd ? (orderedQty - totalReceived) : 0;

      // Default: full revert → remaining after = poRemaining + grReceived
      const initialRemaining = poRemainingQty + grReceivedQty;

      return `
          <tr class="hover:bg-blue-100">
            <td class="px-2 py-2 font-mono text-gray-800">${item.itemCode}</td>
            <td class="px-2 py-2 text-right">
              <span class="bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">${orderedQty}</span>
            </td>
            <td class="px-2 py-2 text-right">
              <span class="bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-semibold">${grReceivedQty}</span>
            </td>
            <td class="px-2 py-2 text-right">
              <span class="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-semibold">${poRemainingQty}</span>
            </td>
            <td class="px-2 py-2 text-center">
              <input 
                type="number" 
                id="revert-qty-${index}"
                class="w-20 px-2 py-1 text-center border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                max="${grReceivedQty}"
                value="${grReceivedQty}"
                data-item-id="${item.id}"
                disabled
                data-item-code="${item.itemCode}"
                data-gr-received="${grReceivedQty}"
                data-po-remaining="${poRemainingQty}"
              />
            </td>
            <td class="px-2 py-2 text-right">
              <span 
                id="remaining-after-${index}"
                class="bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold"
              >
                ${initialRemaining}
              </span>
            </td>
            <td class="px-2 py-2">
              <span class="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-semibold">
                ${item.warehouseCode || 'N/A'}
              </span>
            </td>
          </tr>
        `}).join('')}
      </tbody>
    </table>
    <div class="mt-3 pt-3 border-t border-blue-200">
      <p class="text-xs text-blue-700">
        💡 <strong>Tip:</strong> Enter the quantity you want to revert. Leave as is to revert all.<br/>
        📊 <strong>Formula:</strong> Remaining After Revert = PO Remaining (Latest) + Qty to Revert
      </p>
    </div>
  </div>
` : '';

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Revert GR?',
      html: `
    <div class="text-left">
      <p class="mb-3 text-gray-700">Specify quantities to revert for <strong class="text-indigo-600">${gr.grNo}</strong></p>
      
      <div class="bg-red-50 border-l-4 border-red-400 rounded-r p-3 mb-4">
        <p class="text-red-800 font-semibold text-sm flex items-center gap-2">
          <span>⚠️</span> This action cannot be undone!
        </p>
      </div>

      <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <p class="font-bold text-amber-800 mb-3 flex items-center gap-2">
          <span>📦</span> What will happen:
        </p>
        <ul class="space-y-2 text-sm text-gray-700">
          <li class="flex items-start gap-2">
            <span class="text-amber-600 font-bold">1.</span>
            <span><strong>GR Status:</strong> Will be marked as "Reverted" (Status: -2)</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-amber-600 font-bold">2.</span>
            <span><strong>GR Details:</strong> ReceivedQty will be <strong>decreased</strong> by the amounts you specify</span>
          </li>
          ${gr.poId ? `
            <li class="flex items-start gap-2">
              <span class="text-amber-600 font-bold">3.</span>
              <span><strong>PO Details:</strong> TotalReceivedQty will be <strong>decreased</strong> accordingly</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-amber-600 font-bold">4.</span>
              <span><strong>PO Remaining:</strong> Will <strong>increase</strong> by reverted qty</span>
            </li>
          ` : `
            <li class="flex items-start gap-2">
              <span class="text-amber-600 font-bold">3.</span>
              <span><strong>Manual GR:</strong> No PO to restore (created without PO)</span>
            </li>
          `}
          <li class="flex items-start gap-2">
            <span class="text-amber-600 font-bold">${gr.poId ? '5' : '4'}.</span>
            <span><strong>Inventory:</strong> Quantities will be <strong>deducted</strong> from stock</span>
          </li>
        </ul>
      </div>

      ${inputFieldsHTML}

      ${gr.poId && poDetails.length > 0 ? `
        <div class="bg-indigo-50 border border-indigo-200 rounded-lg p-4 max-h-64 overflow-y-auto">
          <p class="font-bold text-indigo-800 mb-3 flex items-center gap-2">
            <span>🔄</span> PO Impact Preview:
          </p>
          <table class="w-full text-xs">
            <thead class="bg-indigo-100 sticky top-0">
              <tr>
                <th class="px-2 py-1 text-left font-semibold text-indigo-900">Item Code</th>
                <th class="px-2 py-1 text-right font-semibold text-indigo-900">PO Remaining (Latest)</th>
                <th class="px-2 py-1 text-right font-semibold text-indigo-900">After Full Revert</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-indigo-100">
              ${gr.grDetails.map((grItem, index) => {
        const pd = poDetailMap[grItem.itemCode];
        if (!pd) return '';
        const ordQty = pd.qty ?? 0;
        const totalRec = pd.totalReceivedQty ?? 0;
        const poRemaining = ordQty - totalRec;
        const afterRevert = poRemaining + (grItem.receivedQty || 0);
        return `
                  <tr class="hover:bg-indigo-100">
                    <td class="px-2 py-2 font-mono text-gray-800">${grItem.itemCode}</td>
                    <td class="px-2 py-2 text-right">
                      <span class="bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-semibold">${poRemaining}</span>
                    </td>
                    <td class="px-2 py-2 text-right">
                      <span class="bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold" id="po-result-${index}">
                        ${afterRevert}
                      </span>
                    </td>
                  </tr>
                `;
      }).filter(Boolean).join('')}
            </tbody>
          </table>
          <div class="mt-3 pt-3 border-t border-indigo-200">
            <p class="text-xs text-indigo-700 font-semibold">
              📌 PO Number: <span class="font-mono text-indigo-900">${gr.poNumber}</span>
            </p>
          </div>
        </div>
      ` : ''}
    </div>
  `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Revert Now',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      width: '1000px',
      customClass: { popup: 'text-sm', htmlContainer: 'overflow-visible' },
      didOpen: () => {
        gr.grDetails.forEach((item, index) => {
          const input = document.getElementById(`revert-qty-${index}`);
          const remainingSpan = document.getElementById(`remaining-after-${index}`);
          const poResult = document.getElementById(`po-result-${index}`);

          const grReceivedQty = item.receivedQty ?? 0;
          const pd = poDetailMap[item.itemCode];
          const ordQty = pd ? (pd.qty ?? 0) : 0;
          const totalRec = pd ? (pd.totalReceivedQty ?? 0) : 0;
          const poRemainingQty = ordQty - totalRec; // ✅ latest from DB

          if (input) {
            input.addEventListener('input', (e) => {
              const qtyToRevert = Math.min(
                Math.max(parseInt(e.target.value) || 0, 0),
                grReceivedQty
              );

              const remaining = poRemainingQty + qtyToRevert;

              if (remainingSpan) {
                remainingSpan.textContent = remaining;
                remainingSpan.className =
                  qtyToRevert === grReceivedQty
                    ? 'bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold'
                    : qtyToRevert > 0
                      ? 'bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded font-bold'
                      : 'bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-bold';
              }

              if (poResult) {
                poResult.textContent = poRemainingQty + qtyToRevert;
              }
            });
          }
        });
      }
    });

    if (!result.isConfirmed) return;

    // Collect revert quantities
    const revertItems = gr.grDetails.map((item, index) => {
      const inputEl = document.getElementById(`revert-qty-${index}`);
      const qtyToRevert = inputEl ? parseInt(inputEl.value) || 0 : item.receivedQty;
      return {
        id: item.id,
        itemCode: item.itemCode,
        qtyToRevert: qtyToRevert,
        warehouseCode: item.warehouseCode
      };
    }).filter(item => item.qtyToRevert > 0);

    if (revertItems.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Quantities Specified',
        text: 'Please enter at least one quantity to revert.',
        confirmButtonColor: '#f59e0b'
      });
      return;
    }

    setRevertingId(gr.id);

    try {
      const res = await fetch(`${API_BASE_URL}api/GR_Header/Revert/${gr.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ revertItems })
      });

      const responseText = await res.text();

      if (!res.ok) {
        let errorData;
        try { errorData = JSON.parse(responseText); }
        catch { errorData = { message: responseText || `Server error (${res.status})` }; }
        throw new Error(
          errorData?.message || errorData?.error || errorData?.inner ||
          `Revert failed with status ${res.status}`
        );
      }

      const totalReverted = revertItems.reduce((sum, item) => sum + item.qtyToRevert, 0);

      Swal.fire({
        icon: 'success',
        title: 'Reverted Successfully!',
        html: `
      <div class="text-left">
        <p class="mb-4 text-gray-700">
          <strong class="text-indigo-600">${gr.grNo}</strong> has been reverted successfully.
        </p>
        <div class="bg-green-50 border border-green-200 rounded-lg p-4">
          <p class="font-bold text-green-800 mb-3">✅ Changes Applied:</p>
          <div class="space-y-2 text-sm text-gray-700">
            <div class="flex items-center justify-between p-2 bg-white rounded">
              <span class="font-semibold">📦 GR Status:</span>
              <span class="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-bold">REVERTED</span>
            </div>
            <div class="flex items-center justify-between p-2 bg-white rounded">
              <span class="font-semibold">🔢 Items Reverted:</span>
              <span class="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">${revertItems.length} item(s)</span>
            </div>
            <div class="flex items-center justify-between p-2 bg-white rounded">
              <span class="font-semibold">📊 Total Qty Reverted:</span>
              <span class="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-bold">${totalReverted} units</span>
            </div>
            ${gr.poNumber ? `
              <div class="flex items-center justify-between p-2 bg-white rounded">
                <span class="font-semibold">🔄 PO Updated:</span>
                <span class="bg-indigo-100 text-indigo-700 px-2 py-1 rounded text-xs font-bold font-mono">${gr.poNumber}</span>
              </div>
            ` : ''}
            <div class="flex items-center justify-between p-2 bg-white rounded">
              <span class="font-semibold">📉 Inventory:</span>
              <span class="text-red-600 font-semibold">Quantities Deducted ✓</span>
            </div>
          </div>
          <div class="mt-4 p-3 bg-white rounded-lg border border-green-200">
            <p class="text-xs font-bold text-green-800 mb-2">Items Reverted:</p>
            <div class="space-y-1">
              ${revertItems.map(item => `
                <div class="flex items-center justify-between text-xs">
                  <span class="font-mono text-gray-700">${item.itemCode}</span>
                  <span class="text-red-600 font-semibold">-${item.qtyToRevert}</span>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
    `,
        confirmButtonColor: '#10b981',
        timer: 6000,
        timerProgressBar: true,
        showConfirmButton: true,
        confirmButtonText: 'Got it',
        width: '650px'
      });

      if (closeModalAfter) setSelectedGR(null);
      await fetchGRList();

      const user = JSON.parse(localStorage.getItem("user"));
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "UPDATE-REVERT",
          module: "Goods Receipt",
          description: `GR ${gr.grNo} was reverted. Items: ${revertItems.length}, Total Qty: ${revertItems.reduce((sum, i) => sum + i.qtyToRevert, 0)}`,
          userId: user?.userId,
          username: user?.username,
          fullName: user?.fullName || user?.username,
        })
      }).catch(err => console.error("Log failed:", err));

    } catch (err) {
      console.error('[REVERT] Error caught:', err);
      Swal.fire({
        icon: 'error',
        title: 'Revert Failed',
        html: `
      <div class="text-left">
        <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-3">
          <p class="font-bold text-red-800 mb-2">❌ Error Details:</p>
          <div class="bg-white rounded p-3">
            <p class="text-sm text-gray-700 font-mono break-words whitespace-pre-wrap">${err.message}</p>
          </div>
        </div>
        <div class="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p class="text-xs text-amber-800 font-semibold mb-2">⚠️ Common Issues:</p>
          <ul class="text-xs text-gray-700 space-y-1">
            <li>• <strong>GR already reverted:</strong> Check if status is already -2</li>
            <li>• <strong>PO not found:</strong> Verify PO exists in database</li>
            <li>• <strong>Network error:</strong> Verify API endpoint is accessible</li>
          </ul>
        </div>
        <div class="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
          💡 Press F12 to view detailed error logs in browser console
        </div>
      </div>
    `,
        confirmButtonColor: '#dc2626',
        confirmButtonText: 'Close',
        width: '650px'
      });
    } finally {
      setRevertingId(null);
    }
  };
  /* ────────────── DELETE ────────────── */
  const handleDelete = async (gr, closeModalAfter = false) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete GR?',
      html: `Are you sure you want to delete <strong>${gr.grNo}</strong>?<br><small class="text-gray-500">This will also remove all ${gr.grDetails?.length || 0} item(s). This action cannot be undone.</small>`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
    });

    if (!result.isConfirmed) return;

    setDeletingId(gr.id);
    try {
      const res = await fetch(`${API_BASE_URL}api/GR_Header/${gr.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.message || `Delete failed (${res.status})`);
      }


      const user = JSON.parse(localStorage.getItem("user"));
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "DELETE",
          module: "Goods Receipt",
          description: `GR ${gr.grNo} was permanently deleted. Items: ${gr.grDetails?.length || 0}`,
          userId: user?.userId,
          username: user?.username,
          fullName: user?.fullName || user?.username,
        })
      }).catch(err => console.error("Log failed:", err));
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        html: `<strong>${gr.grNo}</strong> has been deleted successfully.`,
        confirmButtonColor: '#10b981',
        timer: 2000,
        showConfirmButton: false,
      });

      if (closeModalAfter) {
        setSelectedGR(null);
      }

      fetchGRList();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message,
        confirmButtonColor: '#dc2626',
      });
    } finally {
      setDeletingId(null);
    }
  };

  /* ────────────── clear filters ────────────── */
  const clearFilters = () => {
    setSearchText('');
    setFilterVendor('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const hasActiveFilters =
    searchText.trim() || filterVendor || filterStatus !== '' || filterDateFrom || filterDateTo;

  /* ═══════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 md:p-6">
      <div className="max-w-8xl mx-auto">

        {/* ── TOP BAR ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-indigo-900 flex items-center gap-2">
                <Package className="w-6 h-6 text-indigo-600" />
                Goods Receipt List
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Total records: <span className="font-semibold text-indigo-600">{grList.length}</span>
                {hasActiveFilters && (
                  <span className="ml-2 text-amber-600 font-medium">
                    · Showing {filtered.length} filtered
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => { setShowExportModal(true); fetchExportData(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export GR-PO
              </button>
              <button
                onClick={fetchGRList}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition text-gray-600"
                title="Refresh"
              >
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              <button
                onClick={() => setShowFilters((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition ${showFilters || hasActiveFilters
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 bg-indigo-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {[searchText.trim(), filterVendor, filterStatus !== '', filterDateFrom, filterDateTo].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════
    EXPORT GR-PO MODAL
    ══════════════════════════════ */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col">

              {/* Modal Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-bold">Export GR-PO Report</h3>
                    <p className="text-emerald-200 text-xs">I-filter at i-download ang Excel report</p>
                  </div>
                </div>
                <button onClick={() => setShowExportModal(false)}
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-5">

                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Total GR Records', value: grList.length, color: 'indigo' },
                    { label: 'Total Line Items', value: exportData.length, color: 'emerald' },
                    { label: 'Filtered Items', value: applyPeriodFilter(exportData).length, color: 'amber' },
                  ].map(card => (
                    <div key={card.label} className={`bg-${card.color}-50 border border-${card.color}-200 rounded-xl p-3 text-center`}>
                      <p className={`text-2xl font-bold text-${card.color}-700`}>
                        {exportLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : card.value}
                      </p>
                      <p className={`text-xs text-${card.color}-600 font-medium mt-0.5`}>{card.label}</p>
                    </div>
                  ))}
                </div>

                {/* Filters */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-bold text-gray-700 flex items-center gap-2">
                    <Filter className="w-4 h-4 text-indigo-500" />
                    Filter before Export
                  </p>

                  {/* Period Type Toggle */}
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-2 block">Period Type:</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'week', label: '📅 Weekly' },
                        { value: 'month', label: '🗓 Monthly' },
                        { value: 'year', label: '📆 Yearly' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setExportPeriodType(opt.value);
                            setExportWeek('');
                            setExportMonth('');
                            setExportYear(new Date().getFullYear().toString());
                          }}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border-2 transition ${exportPeriodType === opt.value
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400'
                            }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Vendor */}
                    <div>
                      <label className="text-xs font-semibold text-gray-600 mb-1 block">Vendor:</label>
                      <select value={exportVendor} onChange={e => setExportVendor(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white">
                        <option value="">All Vendors</option>
                        {vendorOptions.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>

                    {/* Week Picker */}
                    {exportPeriodType === 'week' && (
                      <div>
                        <label className="text-xs font-semibold text-gray-600 mb-1 block">Select Week:</label>
                        <input
                          type="week"
                          value={exportWeek}
                          onChange={e => setExportWeek(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        />
                      </div>
                    )}

                    {/* Month Picker */}
                    {exportPeriodType === 'month' && (
                      <div>
                        <label className="text-xs font-semibold text-gray-600 mb-1 block">Select Month:</label>
                        <input
                          type="month"
                          value={exportMonth}
                          onChange={e => setExportMonth(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                        />
                      </div>
                    )}

                    {/* Year Picker */}
                    {exportPeriodType === 'year' && (
                      <div>
                        <label className="text-xs font-semibold text-gray-600 mb-1 block">Select Year:</label>
                        <select
                          value={exportYear}
                          onChange={e => setExportYear(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                        >
                          {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {(exportVendor || exportWeek || exportMonth) && (
                    <button onClick={() => {
                      setExportVendor('');
                      setExportWeek('');
                      setExportMonth('');
                      setExportYear(new Date().getFullYear().toString());
                    }}
                      className="text-xs text-red-500 hover:text-red-700 font-semibold flex items-center gap-1">
                      <X className="w-3 h-3" /> Clear filters
                    </button>
                  )}
                </div>

                {/* Preview table */}
                {!exportLoading && exportData.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-600 mb-2 uppercase tracking-wide">
                      Preview (first 5 rows):
                    </p>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-indigo-50">
                          <tr>
                            {['GR No', 'Vendor', 'PO No', 'Item Code', 'Description', 'PO Qty', 'Delivered', 'Balance'].map(h => (
                              <th key={h} className="px-2 py-2 text-left font-bold text-indigo-700 whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {applyPeriodFilter(exportData).slice(0, 5).map((row, i) => (
                            <tr key={i} className={`border-t border-gray-100 ${i % 2 === 1 ? 'bg-gray-50' : ''}`}>
                              <td className="px-2 py-1.5 font-mono text-indigo-700 font-semibold whitespace-nowrap">{row.grNo}</td>
                              <td className="px-2 py-1.5 whitespace-nowrap">{row.vendorName}</td>
                              <td className="px-2 py-1.5 text-indigo-600 whitespace-nowrap">{row.poNumber || '—'}</td>
                              <td className="px-2 py-1.5 font-mono whitespace-nowrap">{row.itemCode}</td>
                              <td className="px-2 py-1.5 max-w-[120px] truncate">{row.description}</td>
                              <td className="px-2 py-1.5 text-right">{row.poQty?.toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-right text-emerald-700 font-semibold">{row.actualDelivered?.toLocaleString()}</td>
                              <td className={`px-2 py-1.5 text-right font-bold ${row.balanceToDeliver < 0 ? 'text-red-600' :
                                row.balanceToDeliver === 0 ? 'text-gray-400' : 'text-amber-600'
                                }`}>
                                {row.balanceToDeliver?.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {exportLoading && (
                  <div className="flex items-center justify-center py-8 gap-3 text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    <span className="text-sm font-medium">Loading GR & PO data...</span>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <button onClick={() => setShowExportModal(false)}
                  className="text-sm text-gray-500 hover:text-gray-700 font-medium">
                  Cancel
                </button>
                <button
                  onClick={handleExportExcel}
                  disabled={exportLoading || exportData.length === 0}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  Download Excel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── FILTER PANEL ── */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search GR / Vendor / PO…"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              <select
                value={filterVendor}
                onChange={(e) => setFilterVendor(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white text-gray-700"
              >
                <option value="">All Vendors</option>
                {vendorOptions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white text-gray-700"
              >
                <option value="">All Status</option>
                <option value="1">Active</option>
                <option value="2">Approved</option>
                <option value="3">Closed</option>
                <option value="-2">Reverted</option>
              </select>

              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />

              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-3 text-xs text-red-500 hover:text-red-700 font-semibold flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear all filters
              </button>
            )}
          </div>
        )}

        {/* ── TABLE ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

          {loading && (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-3" />
              <p className="text-gray-500 text-sm">Fetching Goods Receipts…</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-24">
              <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-red-600 font-semibold">{error}</p>
              <button onClick={fetchGRList} className="mt-2 text-sm text-indigo-600 underline">Retry</button>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24">
              <Package className="w-14 h-14 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">
                {hasActiveFilters ? 'No results match your filters.' : 'No Goods Receipts yet.'}
              </p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="mt-2 text-sm text-indigo-600 underline">Clear filters</button>
              )}
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-indigo-50 border-b border-indigo-100">
                      <th className="px-4 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider w-10">#</th>
                      {[
                        { key: 'grNo', label: 'GR No' },
                        { key: 'grDate', label: 'GR Date' },
                        { key: 'vendorName', label: 'Vendor' },
                        { key: 'poNumber', label: 'PO No' },
                        { key: 'receivedBy', label: 'Received By' },
                      ].map((col) => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          className="px-4 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider cursor-pointer select-none hover:text-indigo-900 transition"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {col.label}
                            <SortIcon col={col.key} />
                          </span>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Items</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-indigo-700 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-indigo-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginated.map((gr, idx) => {
                      const rowNum = (safeCurrentPage - 1) * pageSize + idx + 1;
                      const isDeleting = deletingId === gr.id;
                      const isReverting = revertingId === gr.id;
                      const isProcessing = isDeleting || isReverting;
                      return (
                        <tr
                          key={gr.id}
                          className={`border-b border-gray-100 transition-colors ${isDeleting ? 'bg-red-50' : isReverting ? 'bg-orange-50' : 'hover:bg-indigo-50'
                            }`}
                        >
                          <td className="px-4 py-3 text-gray-400 font-medium">{rowNum}</td>
                          <td className="px-4 py-3">
                            <span className="font-mono font-semibold text-indigo-800">{gr.grNo}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            <span className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-gray-400" />
                              {formatDate(gr.grDate)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-1.5">
                              <Building2 className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-gray-800 font-medium">{gr.vendorName || '—'}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {gr.poNumber ? (
                              <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md text-xs font-semibold">
                                <FileText className="w-3 h-3" />
                                {gr.poNumber}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs italic">Manual</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-gray-700">{gr.receivedBy || '—'}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center justify-center bg-emerald-50 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-md">
                              {gr.grDetails?.length || 0} item{gr.grDetails?.length !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={gr.status} />
                          </td>

                          {/* ── ACTION BUTTONS (View + Revert + Delete) ── */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              {/* View */}
                              <button
                                onClick={() => openDetail(gr.id)}
                                disabled={isProcessing}
                                className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View
                              </button>

                              {/* Revert - only show if not already reverted */}
                              {gr.status !== -2 && (
                                <button
                                  onClick={() => handleRevert(gr)}
                                  disabled={isProcessing}
                                  className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {isReverting ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  )}
                                  {isReverting ? 'Reverting…' : 'Revert'}
                                </button>
                              )}

                              {/* Delete */}
                              <button
                                onClick={() => handleDelete(gr)}
                                disabled={isProcessing}
                                className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {isDeleting ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                )}
                                {isDeleting ? 'Deleting…' : 'Delete'}
                              </button>

                              {/* Post to Accounting */}
                              {/* {(() => {
                                const isAlreadyPosted = postedPONumbers.has(gr.poNumber) || postedGRNumbers.has(gr.grNo);
                                const isPostingThis = postingGRId === gr.id;

                                if (isAlreadyPosted) {
                                  return (
                                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-500 text-xs font-semibold rounded-lg whitespace-nowrap border border-gray-200">
                                      <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                      Posted
                                    </span>
                                  );
                                }

                                return (
                                  <button
                                    onClick={() => handlePostGRToAccounting(gr)}
                                    disabled={isProcessing || isPostingThis}
                                    className={`inline-flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed ${isPostingThis
                                      ? 'bg-gray-400 cursor-not-allowed'
                                      : 'bg-emerald-600 hover:bg-emerald-700'
                                      }`}
                                  >
                                    {isPostingThis ? (
                                      <><Loader2 className="w-3.5 h-3.5 animate-spin" />Posting...</>
                                    ) : (
                                      <>
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                        Post
                                      </>
                                    )}
                                  </button>
                                );
                              })()} */}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── PAGINATION BAR ── */}
              <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-200 rounded-lg px-2 py-1 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  >
                    {PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <span className="text-gray-400">
                    ({(safeCurrentPage - 1) * pageSize + 1}–{Math.min(safeCurrentPage * pageSize, filtered.length)} of {filtered.length})
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    disabled={safeCurrentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    className="p-1.5 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>

                  <button
                    disabled={safeCurrentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="p-1.5 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {(() => {
                    const pages = [];
                    const range = 2;
                    let start = Math.max(1, safeCurrentPage - range);
                    let end = Math.min(totalPages, safeCurrentPage + range);
                    if (start > 1) {
                      pages.push(1);
                      if (start > 2) pages.push('...');
                    }
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (end < totalPages) {
                      if (end < totalPages - 1) pages.push('...');
                      pages.push(totalPages);
                    }
                    return pages.map((p, i) =>
                      p === '...' ? (
                        <span key={`e${i}`} className="px-1 text-gray-400 text-sm">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`w-8 h-8 rounded-md text-sm font-semibold transition ${p === safeCurrentPage
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          {p}
                        </button>
                      )
                    );
                  })()}

                  <button
                    disabled={safeCurrentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    className="p-1.5 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <button
                    disabled={safeCurrentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className="p-1.5 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════
          DETAIL MODAL  (with Revert + Delete buttons sa footer)
          ═══════════════════════════════════════ */}
      {(selectedGR || detailLoading) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col">

            {/* modal header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-600 text-white px-6 py-4 flex-shrink-0 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5" />
                <div>
                  <h3 className="text-lg font-bold">
                    {selectedGR ? selectedGR.grNo : 'Loading…'}
                  </h3>
                  <p className="text-indigo-200 text-xs">
                    {selectedGR ? formatDateLong(selectedGR.grDate) : '—'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setSelectedGR(null); setDetailLoading(false); }}
                className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* modal body */}
            <div className="flex-1 overflow-y-auto p-6">
              {detailLoading && !selectedGR ? (
                <div className="flex flex-col items-center justify-center py-24">
                  <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-3" />
                  <p className="text-gray-500 text-sm">Loading details…</p>
                </div>
              ) : selectedGR ? (
                <>
                  {/* ── info cards ── */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    {[
                      { icon: Building2, label: 'Vendor', value: selectedGR.vendorName || '—' },
                      { icon: User, label: 'Received By', value: selectedGR.receivedBy || '—' },
                      { icon: FileText, label: 'PO Number', value: selectedGR.poNumber || 'Manual' },
                      { icon: CheckCircle, label: 'Status', value: selectedGR.status === 1 ? 'Active' : selectedGR.status === 2 ? 'Approved' : selectedGR.status === 3 ? 'Closed' : 'Reverted', isStatus: true, status: selectedGR.status },
                    ].map((card, i) => (
                      <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <card.icon className="w-3.5 h-3.5 text-indigo-500" />
                          <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{card.label}</span>
                        </div>
                        {card.isStatus ? (
                          <StatusBadge status={card.status} />
                        ) : (
                          <p className="text-sm font-semibold text-gray-800 truncate">{card.value}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* ── remarks ── */}
                  {selectedGR.remarks && (
                    <div className="mb-5 p-3 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
                      <p className="text-xs text-amber-700 font-bold uppercase mb-0.5">Remarks</p>
                      <p className="text-sm text-gray-700">{selectedGR.remarks}</p>
                    </div>
                  )}

                  {/* ── PO Reference Info ── */}
                  {selectedGR.poNumber && (
                    <div className="mb-5 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                      <p className="text-xs text-blue-700 font-bold uppercase mb-1">📋 Linked Purchase Order</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">PO Number: {selectedGR.poNumber}</p>
                          <p className="text-xs text-gray-600">This GR was created from a Purchase Order</p>
                        </div>
                        {selectedGR.status === -2 && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                            ✓ PO Restored
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── items table ── */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4 text-indigo-600" />
                      Items Received
                      <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
                        {selectedGR.grDetails?.length || 0}
                      </span>
                    </h4>

                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-indigo-50">
                            <th className="border-b border-gray-200 px-3 py-2 text-left text-xs font-bold text-indigo-700 uppercase">#</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left text-xs font-bold text-indigo-700 uppercase">Item Code</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left text-xs font-bold text-indigo-700 uppercase">Description</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-right text-xs font-bold text-indigo-700 uppercase">Ordered</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-right text-xs font-bold text-indigo-700 uppercase">Received</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left text-xs font-bold text-indigo-700 uppercase">UOM</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left text-xs font-bold text-indigo-700 uppercase">Batch</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left text-xs font-bold text-indigo-700 uppercase">Expiry</th>
                            <th className="border-b border-gray-200 px-3 py-2 text-left text-xs font-bold text-indigo-700 uppercase">Warehouse</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedGR.grDetails || []).map((item, idx) => (
                            <tr key={item.id || idx} className="border-b border-gray-100 last:border-0 hover:bg-indigo-50 transition-colors">
                              <td className="px-3 py-2 text-gray-400 font-medium">{idx + 1}</td>
                              <td className="px-3 py-2 font-mono font-semibold text-indigo-800">{item.itemCode}</td>
                              <td className="px-3 py-2 text-gray-700">{item.itemDescription}</td>
                              <td className="px-3 py-2 text-right text-gray-500">{item.orderedQty || '—'}</td>
                              <td className="px-3 py-2 text-right font-bold text-emerald-700">{item.receivedQty}</td>
                              <td className="px-3 py-2 text-gray-600">{item.uom}</td>
                              <td className="px-3 py-2">
                                {item.batchNo ? (
                                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-semibold">{item.batchNo}</span>
                                ) : (
                                  <span className="text-gray-400 text-xs">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-gray-600 text-xs">{formatDate(item.expiryDate)}</td>
                              <td className="px-3 py-2">
                                {item.warehouseCode ? (
                                  <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs font-semibold">{item.warehouseCode}</span>
                                ) : (
                                  <span className="text-gray-400 text-xs">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* ── summary row ── */}
                    <div className="mt-3 flex justify-end">
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 flex items-center gap-4">
                        <span className="text-xs text-indigo-600 font-semibold uppercase">Total Received</span>
                        <span className="text-lg font-bold text-indigo-800">
                          {(selectedGR.grDetails || []).reduce((sum, i) => sum + (Number(i.receivedQty) || 0), 0)}
                        </span>
                        <span className="text-xs text-indigo-400">units</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            {/* ── MODAL FOOTER — Revert + Delete buttons ── */}
            {selectedGR && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                  {/* Revert button - only show if not already reverted */}
                  {selectedGR.status !== -2 && (
                    <button
                      onClick={() => handleRevert(selectedGR, true)}
                      disabled={revertingId === selectedGR.id || deletingId === selectedGR.id}
                      className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {revertingId === selectedGR.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4" />
                      )}
                      {revertingId === selectedGR.id ? 'Reverting…' : 'Revert this GR'}
                    </button>
                  )}

                  {/* Delete button */}
                  <button
                    onClick={() => handleDelete(selectedGR, true)}
                    disabled={deletingId === selectedGR.id || revertingId === selectedGR.id}
                    className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deletingId === selectedGR.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                    {deletingId === selectedGR.id ? 'Deleting…' : 'Delete this GR'}
                  </button>
                </div>

                <button
                  onClick={() => setSelectedGR(null)}
                  className="text-sm text-gray-500 hover:text-gray-700 font-medium transition"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}